import { getStoredRole } from "../../../shared/utils/auth";
import { useEffect, useState } from "react";
import UserModal from "./UserModal";
import { FiCopy } from "react-icons/fi";
import {
  generateResetLink,
  getUsers,
  toggleUserStatus,
  createUser,
} from "../../../services/userService";

import Toast from "../../../shared/components/Toast";
import Pagination from "../../../shared/components/Pagination";
import usePagination from "../../../shared/hooks/usePagination";

const UserPage = () => {
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [loading, setLoading] = useState(true);
  const role = getStoredRole();

  const [toast, setToast] = useState(null);
  const showToast = (message, type = "success") => setToast({ message, type });

  const isEditable =
    (role === "Admin" || role === "Super Admin");

  // ✅ NEW PAGINATION (same as SiteLocation)
  const {
    currentPage,
    totalPages,
    pageData,
    goToPage,
    setPageSize,
    pageSize,
    totalItems,
    startIndex,
    endIndex,
  } = usePagination(users, 6);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await getUsers();
      if (res.code === 200) {
        setUsers(res.data);
      }
    } catch (err) {
      console.error(err);
      showToast(err?.response?.data?.message ||"Failed to load users", "error");
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditData(null);
    setShowModal(true);
  };

  const close = () => {
    setShowModal(false);
    setEditData(null);
  };

  // SAVE USER
  const handleSaved = async (form, isEdit) => {
    if (form?.error) {
      showToast(form.error, "error");
      return;
    }

    try {
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        role_id: Number(form.role) || 2,
      };

      const res = await createUser(payload);

      if (res.code === 200) {
        fetchUsers();
        showToast(res.message || "User added successfully", "success");
      }

      close();
    } catch (err) {
      showToast(err.response?.data?.message || "Something went wrong", "error");
    }
  };

  // TOGGLE STATUS
  const handleToggle = async (user) => {
    try {
      const res = await toggleUserStatus(user.id);

      if (res.code === 200) {
        const updatedStatus = res.data.status;

        setUsers((prev) =>
          prev.map((u) =>
            u.id === user.id ? { ...u, status: updatedStatus } : u
          )
        );

        showToast(res.message || "User status updated", "success");
      }
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to update status", "error");
    }
  };

  // RESET password link generation
  const handleResetClick = async (user) => {
    try {
      const res = await generateResetLink(user.id);

      if (res?.status === "success") {
        showToast(res.message || "Reset link generated and sent to email", "success");
      } else {
        showToast(res.message || "Failed to generate reset link", "error");
      }
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to generate reset link", "error");
    }
  };

  //copy link
  const handleCopyLink = (user) => {
  if (!user.temp_reset_token) {
    showToast("Reset link not available", "error");
    return;
  }

  const link = `https://brsrcompass.digioxide.com/reset-password?token=${user.temp_reset_token}&email=${user.email}`;

  navigator.clipboard.writeText(link);
  showToast("Reset link copied", "success");
};

  if (loading)
    return (
      <div className="dash-loader-wrapper">
        <div className="dash-loader"></div>
        <p className="dash-loader-text">User Loading...</p>
      </div>
    );

  return (
    <div className="dash-page">
      <div className="le-page-header">
        <div>
          <div className="dash-page-title">Users</div>
          <div className="dash-page-subtitle">
            Manage login and workflow access
          </div>
        </div>
        {isEditable && (
        <button className="org-btn-save le-add-btn btn-color" onClick={openAdd}>
          + Add User
        </button>
        )}
      </div>

      <div className="le-card">
        {users.length === 0 ? (
          <div className="le-empty">
            <div className="le-empty-text">No users added yet.</div>
          </div>
        ) : (
          <>
            <div className="table-wrap">
              <table className="le-table">
                <thead>
                  <tr>
                    
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Role</th>
                    <th>Password</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {pageData.map((u, i) => (
                    <tr key={u.id}>
                     
                      <td data-label="Name" className="le-entity-name">
                        {u.name}
                      </td>
                      <td data-label="Email">{u.email}</td>
                      <td data-label="Phone">{u.phone || "—"}</td>

                      <td data-label="Role">
                        <span className="role-pill">{u.role?.name}</span>
                      </td>

                      <td data-label="Password">
                        <button
                          className="reset-btn"
                          disabled={!u.needs_password_reset}
                          style={{
                            opacity: u.needs_password_reset ? 1 : 0.5,
                            cursor: u.needs_password_reset
                              ? "pointer"
                              : "not-allowed",
                          }}
                          onClick={() =>
                            u.needs_password_reset && handleResetClick(u)
                          }
                        >
                          Generate Password Link
                        </button>

                        {/* Modern Copy Icon */}
                        <button
                          className="copy-btn"
                          title="Copy Reset Link"
                          disabled={!u.temp_reset_token}
                          onClick={() => handleCopyLink(u)}
                          style={{
                            opacity: u.temp_reset_token ? 1 : 0.4,
                            cursor: u.temp_reset_token
                              ? "pointer"
                              : "not-allowed",
                            border: "none",
                            background: "transparent",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <FiCopy size={18} />
                        </button>
                      </td>

                      <td data-label="Status">
                        <span
                          className={`status-pill ${
                            u.status === "Active" ? "active" : "inactive"
                          }`}
                        >
                          {u.status}
                        </span>
                      </td>

                      <td data-label="Action">
                        <div className="toggle-wrap">
                          <span className="toggle-label">
                            {u.status === "Active" ? "Disable" : "Enable"}
                          </span>

                          <label
                            className="switch"
                            style={{
                              opacity: u.needs_password_reset ? 1 : 0.5,
                              cursor: u.needs_password_reset
                                ? "pointer"
                                : "not-allowed",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={u.status === "Active"}
                              disabled={!u.needs_password_reset}
                              onChange={() =>
                                u.needs_password_reset && handleToggle(u)
                              }
                            />
                            <span className="slider"></span>
                          </label>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/*  NEW PAGINATION UI */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              startIndex={startIndex}
              endIndex={endIndex}
              onPageChange={goToPage}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
            />
          </>
        )}
      </div>

      {/* MODAL */}
      {showModal && (
        <UserModal
          onClose={close}
          onSaved={handleSaved}
          editData={editData}
          showToast={showToast}
        />
      )}

      {/* TOAST */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default UserPage;
